import React, {memo, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faUser} from "@fortawesome/free-solid-svg-icons/faUser";
import {isEmpty} from "es-toolkit/compat";

import RenderIf from "@/components/Global/RenderIf/RenderIf";
import Alert from "@/components/Global/Alert/Alert";
import CustomerPictureContainer from "./CustomerPictureContainer";

import {readFileAsBase64} from "@/helpers/readFileAsBase64";
import {uploadProfileImage} from "@/features/user";

import styles from "./CustomerPicture.module.scss";
import {selectUserDetails} from "@/features/user/userSelectors";
import {selectPartnersDetails} from "@/features/partners/partnersSelectors";

export default memo(function CustomerPicture({
  showShadow = true,
  showMarks = false,
  showPartner = false,
  allowEdit = false,
  className = "",
  whiteLabelLogo = "",
}) {
  const [errorMessage, setErrorMessage] = useState("");
  const userDetails = useSelector(selectUserDetails);
  const partnerDetails = useSelector(selectPartnersDetails);

  const hasPartnerAssigned = window.config.isWhiteLabelApp ? false : userDetails?.partnerId != 0 || false;
  const partnerLogo = useMemo(() => {
    if (isEmpty(partnerDetails) || !partnerDetails?.partnerLogo) return null;
    if (partnerDetails?.partnerLogo?.startsWith("https://")) return partnerDetails?.partnerLogo;

    return `https://sys.forthwithlife.co.uk${partnerDetails?.partnerLogo}`;
  }, [partnerDetails]);

  const userPicture = useMemo(() => {
    if (!userDetails.customerImage) {
      return;
    }
    if (
      userDetails.customerImage.startsWith("data:image/") ||
      userDetails.customerImage.startsWith("https://forth-image-uploads.s3.")
    ) {
      return userDetails.customerImage;
    }
    if (userDetails.customerImage.includes("/uploads/")) {
      return `${import.meta.env.VITE_API_ROOT}${userDetails.customerImage}`;
    }
    return "https://www.forthwithlife.co.uk" + userDetails.customerImage;
  }, [userDetails]);

  const dispatch = useDispatch();

  const handleError = imageFile => {
    let error = false;

    if (!["image/png", "image/jpeg", "image/jpg", "image/webp"].some(type => imageFile.type == type)) {
      setErrorMessage("Incorrect image type - Image must be a .png, .jpg, .jpeg, or .webp");
      error = true;
    }

    if (imageFile.size / 1024 / 1024 > 5) {
      setErrorMessage("Image size too large - File must be less than 5MB");
      error = true;
    }

    return error;
  };

  const handlePictureUpload = async imageFile => {
    const error = handleError(imageFile);
    if (!error) {
      setErrorMessage("");
      const pictureString = await readFileAsBase64(imageFile, true);
      dispatch(uploadProfileImage(pictureString));
    }
  };

  return (
    <>
      <CustomerPictureContainer
        showMarks={showMarks}
        showShadow={window.config.isWhiteLabelApp ? false : showShadow}
        allowEdit={allowEdit}
        className={className}
        handlePictureUpload={handlePictureUpload}>
        <RenderIf isTrue={whiteLabelLogo || userDetails.customerImage}>
          <img
            src={whiteLabelLogo ? whiteLabelLogo : userPicture}
            alt={`${userDetails.customerFirstName} profile`}
            className={`img-fluid ${styles.profileImage}`}
            data-white-label={whiteLabelLogo ? true : false}
          />
        </RenderIf>

        <RenderIf isTrue={!userDetails.customerImage}>
          <FontAwesomeIcon icon={faUser} className={styles.fontSize} />
        </RenderIf>
      </CustomerPictureContainer>

      <RenderIf isTrue={hasPartnerAssigned && showPartner && partnerLogo && !isEmpty(partnerDetails)}>
        {/* TODO: PUT HERE THE PARTNER PICTURE */}
        <div className={styles.partnerPicture}>
          <img src={partnerLogo} alt={`${partnerDetails?.partnerName} logo`} />
        </div>
      </RenderIf>

      <RenderIf isTrue={errorMessage != ""}>
        <Alert variant="danger" className="text-center mt-3">
          {errorMessage}
        </Alert>
      </RenderIf>
    </>
  );
});
